import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { clientsApi, Client } from '../api/clients';
import './Clients.css';

const Clients: React.FC = () => {
  const navigate = useNavigate();
  const [clients, setClients] = useState<Client[]>([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [currentPage, setCurrentPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [totalItems, setTotalItems] = useState(0);
  const [error, setError] = useState<string | null>(null);
  const [selectedClients, setSelectedClients] = useState<Set<string>>(new Set());

  const pageSize = 20;

  // Fetch clients
  const fetchClients = useCallback(async () => {
    try {
      setLoading(true);
      setError(null);
      const response = await clientsApi.getClients(currentPage, pageSize, searchTerm);
      setClients(response.clients);
      setTotalPages(response.pagination.totalPages);
      setTotalItems(response.pagination.totalItems);
    } catch (err: any) {
      setError(err.message || 'Failed to fetch clients');
      console.error('Error fetching clients:', err);
    } finally {
      setLoading(false);
    }
  }, [currentPage, searchTerm]);

  useEffect(() => {
    fetchClients();
  }, [fetchClients]);

  // Handle search
  const handleSearch = (e: React.FormEvent) => {
    e.preventDefault();
    setCurrentPage(1);
    fetchClients();
  };

  // Handle client selection
  const handleSelectClient = (clientId: string) => {
    const newSelected = new Set(selectedClients);
    if (newSelected.has(clientId)) {
      newSelected.delete(clientId);
    } else {
      newSelected.add(clientId);
    }
    setSelectedClients(newSelected);
  };

  // Handle select all
  const handleSelectAll = () => {
    if (selectedClients.size === clients.length) {
      setSelectedClients(new Set());
    } else {
      setSelectedClients(new Set(clients.map(c => c.id)));
    }
  };

  // Navigate to client detail
  const handleViewClient = (clientId: string) => {
    navigate(`/clients/${clientId}`);
  };

  // Get verification status badge
  const getVerificationBadge = (status?: string | null) => {
    switch (status) {
      case 'verified':
        return <span className="badge badge-success">Verified</span>;
      case 'pending':
        return <span className="badge badge-warning">Pending</span>;
      case 'rejected':
        return <span className="badge badge-error">Rejected</span>;
      default:
        return <span className="badge badge-secondary">Not Requested</span>;
    }
  };

  return (
    <div className="clients-page">
      <div className="page-header">
        <h1>Clients</h1>
        <div className="header-actions">
          <button 
            className="btn btn-primary"
            onClick={() => navigate('/clients/new')}
          >
            Add Client
          </button>
        </div>
      </div>

      <div className="clients-controls">
        <form onSubmit={handleSearch} className="search-form">
          <input
            type="text"
            placeholder="Search by name, email, or phone..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            className="search-input"
          />
          <button type="submit" className="btn btn-secondary">
            Search
          </button>
          {searchTerm && (
            <button
              type="button"
              className="btn btn-text"
              onClick={() => {
                setSearchTerm('');
                setCurrentPage(1);
              }}
            >
              Clear
            </button>
          )}
        </form>

        <div className="results-info">
          Showing {clients.length} of {totalItems} clients
        </div>
      </div>

      {error && (
        <div className="error-message">
          {error}
        </div>
      )}

      {loading ? (
        <div className="loading-container">
          <div className="spinner"></div>
          <p>Loading clients...</p>
        </div>
      ) : (
        <>
          <div className="clients-table-container">
            <table className="clients-table">
              <thead>
                <tr>
                  <th>
                    <input
                      type="checkbox"
                      checked={clients.length > 0 && selectedClients.size === clients.length}
                      onChange={handleSelectAll}
                    />
                  </th>
                  <th>Name</th>
                  <th>Email</th>
                  <th>Phone</th>
                  <th>City</th>
                  <th>Claims</th>
                  <th>ID Status</th>
                  <th>Created</th>
                  <th>Actions</th>
                </tr>
              </thead>
              <tbody>
                {clients.map((client) => (
                  <tr key={client.id}>
                    <td>
                      <input
                        type="checkbox"
                        checked={selectedClients.has(client.id)}
                        onChange={() => handleSelectClient(client.id)}
                      />
                    </td>
                    <td>
                      <button
                        className="link-button"
                        onClick={() => handleViewClient(client.id)}
                      >
                        {client.firstName} {client.lastName}
                      </button>
                    </td>
                    <td>{client.email}</td>
                    <td>{client.phoneNumber || '-'}</td>
                    <td>{client.city || '-'}</td>
                    <td>
                      <span className="claim-count">
                        {client.claimCount || 0}
                      </span>
                    </td>
                    <td>{getVerificationBadge(client.idVerificationStatus)}</td>
                    <td>
                      {client.createdAt
                        ? new Date(client.createdAt).toLocaleDateString()
                        : '-'}
                    </td>
                    <td>
                      <button
                        className="btn btn-small btn-secondary"
                        onClick={() => handleViewClient(client.id)}
                      >
                        View
                      </button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>

          {totalPages > 1 && (
            <div className="pagination">
              <button
                className="btn btn-secondary"
                onClick={() => setCurrentPage(p => Math.max(1, p - 1))}
                disabled={currentPage === 1}
              >
                Previous
              </button>
              <span className="page-info">
                Page {currentPage} of {totalPages}
              </span>
              <button
                className="btn btn-secondary"
                onClick={() => setCurrentPage(p => Math.min(totalPages, p + 1))}
                disabled={currentPage === totalPages}
              >
                Next
              </button>
            </div>
          )}
        </>
      )}

      {selectedClients.size > 0 && (
        <div className="bulk-actions-bar">
          <span>{selectedClients.size} client(s) selected</span>
          <button
            className="btn btn-primary"
            onClick={() => {
              // TODO: Implement bulk ID request
              console.log('Send ID request to:', Array.from(selectedClients));
            }}
          >
            Send ID Request
          </button>
          <button
            className="btn btn-text"
            onClick={() => setSelectedClients(new Set())}
          >
            Clear Selection
          </button>
        </div>
      )}
    </div>
  );
};

export default Clients;